import * as React from "react";
import { AlertTriangle } from "lucide-react";
import type { FunctionKey, MaturityLabel } from "@/lib/recommendationMap";
import { riskComparator, calculatePriority, priorityLabelFromWeight, priorityColor } from "@/lib/riskOrdering";

export type RiskItem = {
  title: string;
  nistFunction: FunctionKey | null;
  score: number;
  maturity: MaturityLabel | "";
  priorityWeight?: string;
  questionIndex?: number;
};

export function TopStrategicRisks({ risks }: { risks: RiskItem[] }) {
  if (!risks.length) return null;

  // Compute residual priority for display/sorting (inherent weight + maturity)
  const normalized = risks.map((risk) => ({
    ...risk,
    residualPriority: calculatePriority(risk.priorityWeight ?? "medium", risk.maturity || null),
  }));

  // Sort using centralized risk comparator on residual priority
  const sorted = [...normalized].sort((a, b) =>
    riskComparator(
      { priorityWeight: a.residualPriority, score: a.score, questionIndex: a.questionIndex ?? 0 },
      { priorityWeight: b.residualPriority, score: b.score, questionIndex: b.questionIndex ?? 0 },
    )
  );

  return (
    <section className="top-risks-section avoid-break rounded-lg border border-border p-4">
      <h2 className="text-base font-bold mb-2 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-red-600" />
        Top Strategic Risks
      </h2>
      <div className="space-y-2">
        {sorted.map((risk, idx) => {
          const badge = priorityLabelFromWeight(risk.residualPriority);
          return (
            <div key={idx} className="flex items-start gap-2 text-sm">
              <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-red-100 text-red-700 text-xs font-bold flex items-center justify-center">
                {idx + 1}
              </span>
              <div className="flex-1">
                <span className="font-medium">{risk.title}</span>
                {risk.nistFunction && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    ({risk.nistFunction})
                  </span>
                )}
              </div>
              <span className={`flex-shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${priorityColor(badge)}`}>
                {badge}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
